import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, FileSignature, Pencil, Trash2, Lock } from "lucide-react";

const VARS = ["NUMERO_PRATICA", "NUMERO_SINISTRO_COMPAGNIA", "NOME_CLIENTE", "DATA_SINISTRO", "COMPAGNIA", "NUMERO_POLIZZA", "RAMO", "DESCRIZIONE", "CONTROPARTE", "REFERENTE", "IMPORTO_RICHIESTO", "DATA_OGGI"];
const empty = { nome: "", tipo: "personalizzato", oggetto: "", destinatario: "", corpo: "" };

export default function ModuliSinistri() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = () => api.get("/sinistri/templates").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const setD = (k, v) => setDialog((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      if (dialog.template_id) await api.put(`/sinistri/templates/${dialog.template_id}`, dialog);
      else await api.post("/sinistri/templates", dialog);
      toast.success("Modulo salvato"); setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (t) => {
    if (!window.confirm(`Eliminare il modulo "${t.nome}"?`)) return;
    try { await api.delete(`/sinistri/templates/${t.template_id}`); toast.success("Modulo eliminato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate("/gestione-sinistri")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Torna ai sinistri</button>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="flex items-center gap-2"><FileSignature className="h-6 w-6 text-red-600" /><h1 className="font-heading text-2xl font-medium text-slate-900">Libreria Moduli</h1></div>
            <p className="text-slate-500 text-sm mt-1">Modelli di lettere e comunicazioni con variabili automatiche</p>
          </div>
          <Button onClick={() => setDialog({ ...empty })} className="bg-red-600 hover:bg-red-700 text-white" data-testid="tpl-add-btn"><Plus className="h-4 w-4 mr-2" /> Nuovo modulo</Button>
        </div>

        <div className="space-y-3" data-testid="tpl-list">
          {items.map((t) => (
            <div key={t.template_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm flex items-start justify-between gap-4" data-testid={`tpl-row-${t.template_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{t.nome}</span>
                  {t.is_default && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 flex items-center gap-1"><Lock className="h-3 w-3" /> predefinito</span>}
                </div>
                <p className="text-sm text-slate-500 mt-1 truncate">{t.oggetto}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => setDialog({ ...t })} data-testid={`tpl-edit-${t.template_id}`}><Pencil className="h-4 w-4" /></Button>
                {!t.is_default && <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => del(t)} data-testid={`tpl-del-${t.template_id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      </main>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{dialog?.template_id ? "Modifica modulo" : "Nuovo modulo"}</DialogTitle>
            <DialogDescription>Usa le variabili tra doppie graffe: verranno sostituite con i dati della pratica.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3 py-2">
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Nome modulo</Label><Input value={dialog.nome} onChange={(e) => setD("nome", e.target.value)} data-testid="tpl-nome" /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Destinatario</Label><Textarea rows={2} value={dialog.destinatario} onChange={(e) => setD("destinatario", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Oggetto</Label><Input value={dialog.oggetto} onChange={(e) => setD("oggetto", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Testo</Label><Textarea rows={10} value={dialog.corpo} onChange={(e) => setD("corpo", e.target.value)} data-testid="tpl-corpo" /></div>
              <div className="text-xs text-slate-400">
                <span className="font-medium">Variabili disponibili:</span>{" "}
                {VARS.map((v) => <code key={v} className="mr-1 text-slate-500">{`{{${v}}}`}</code>)}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} disabled={!dialog?.nome} className="bg-red-600 hover:bg-red-700 text-white" data-testid="tpl-save">Salva modulo</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
